// One shape for every error the customer sees (docs/ERROR_MESSAGES.md in lunchteam-ui-errors):
//   title     what failed, from the customer's side
//   message   why, in one sentence, only when we know (may be empty)
//   guidance  what to do next
//   code      stable AREA-REASON code support can search for
//   reference "CODE · 26 Oct 12:41 · 7f3a9c21": the line the customer reads out or copies
// plus serverId (full, for the copy action and the support composer), at (when it happened),
// status and retrySafe.
//
// Never shows raw server text: callers that know the reason pass their own copy.

export const ERROR_CODES = {
  NETWORK_OFFLINE: 'NETWORK-OFFLINE',
  NETWORK_TIMEOUT: 'NETWORK-TIMEOUT',
  SERVER_UNAVAILABLE: 'SERVER-UNAVAILABLE',
  SERVER_UNEXPECTED: 'SERVER-UNEXPECTED',
  AUTH_SIGNED_OUT: 'AUTH-SIGNED-OUT',
  AUTH_LINK_EXPIRED: 'AUTH-LINK-EXPIRED',
  AUTH_LINK_USED: 'AUTH-LINK-USED',
  AUTH_WRONG_PASSWORD: 'AUTH-WRONG-PASSWORD',
  AUTH_LOCKED: 'AUTH-LOCKED',
  AUTH_LINK_NOT_ALLOWED: 'AUTH-LINK-NOT-ALLOWED',
  AUTH_RESET_LINK_INVALID: 'AUTH-RESET-LINK-INVALID',
  AUTH_PASSWORD_CHANGE_FAILED: 'AUTH-PASSWORD-CHANGE-FAILED',
  ACCOUNT_NOT_FOUND: 'ACCOUNT-NOT-FOUND',
  ACCOUNT_COMPANY_PENDING: 'ACCOUNT-COMPANY-PENDING',
  ACCOUNT_UPDATE_FAILED: 'ACCOUNT-UPDATE-FAILED',
  ACCOUNT_RETAIL_ONLY: 'ACCOUNT-RETAIL-ONLY',
  ACCOUNT_COMPANY_NOT_SERVED: 'ACCOUNT-COMPANY-NOT-SERVED',
  ACCOUNT_EMAIL_INVALID: 'ACCOUNT-EMAIL-INVALID',
  ACCOUNT_SIGNUP_FAILED: 'ACCOUNT-SIGNUP-FAILED',
  ACCOUNT_DETAILS_INVALID: 'ACCOUNT-DETAILS-INVALID',
  ACCOUNT_UNSUBSCRIBE_FAILED: 'ACCOUNT-UNSUBSCRIBE-FAILED',
  MENU_LOAD_FAILED: 'MENU-LOAD-FAILED',
  MENU_RESTAURANT_CLOSED: 'MENU-RESTAURANT-CLOSED',
  MENU_RESTAURANT_UNAVAILABLE: 'MENU-RESTAURANT-UNAVAILABLE',
  MENU_ITEM_SOLD_OUT: 'MENU-ITEM-SOLD-OUT',
  MENU_OPTIONS_MAX: 'MENU-OPTIONS-MAX',
  MENU_OPTIONS_REQUIRED: 'MENU-OPTIONS-REQUIRED',
  ORDER_CUTOFF_PASSED: 'ORDER-CUTOFF-PASSED',
  ORDER_MIN_NOT_MET: 'ORDER-MIN-NOT-MET',
  ORDER_MAX_EXCEEDED: 'ORDER-MAX-EXCEEDED',
  ORDER_TIME_MISSING: 'ORDER-TIME-MISSING',
  ORDER_GUEST_NOT_ALLOWED: 'ORDER-GUEST-NOT-ALLOWED',
  ORDER_DUPLICATE: 'ORDER-DUPLICATE',
  ORDER_PLACE_FAILED: 'ORDER-PLACE-FAILED',
  ORDER_CANCEL_FAILED: 'ORDER-CANCEL-FAILED',
  ORDER_UNKNOWN_STATE: 'ORDER-UNKNOWN-STATE',
  ORDER_CHECKING: 'ORDER-CHECKING',
  ORDER_PLACED_INCOMPLETE: 'ORDER-PLACED-INCOMPLETE',
  PAYMENT_DECLINED: 'PAYMENT-DECLINED',
  PAYMENT_3DS_FAILED: 'PAYMENT-3DS-FAILED',
  PAYMENT_UNKNOWN_STATE: 'PAYMENT-UNKNOWN-STATE',
  PAYMENT_CARD_EXPIRED: 'PAYMENT-CARD-EXPIRED',
  PAYMENT_ON_ACCOUNT_UNAVAILABLE: 'PAYMENT-ON-ACCOUNT-UNAVAILABLE',
  PAYMENT_ALREADY_PROCESSED: 'PAYMENT-ALREADY-PROCESSED',
  PAYMENT_DETAILS_INVALID: 'PAYMENT-DETAILS-INVALID',
  VOUCHER_INVALID: 'VOUCHER-INVALID',
  VOUCHER_EXPIRED: 'VOUCHER-EXPIRED',
  VOUCHER_USED: 'VOUCHER-USED',
  VOUCHER_EMAIL_NOT_REGISTERED: 'VOUCHER-EMAIL-NOT-REGISTERED',
  VOUCHER_EMAIL_NO_COMPANY: 'VOUCHER-EMAIL-NO-COMPANY',
  DELIVERY_TRACKING_UNAVAILABLE: 'DELIVERY-TRACKING-UNAVAILABLE',
  SUPPORT_SEND_REJECTED: 'SUPPORT-SEND-REJECTED',
  SUPPORT_OFF: 'SUPPORT-OFF',
  SUPPORT_TOO_LARGE: 'SUPPORT-TOO-LARGE',
  SUPPORT_RATE_LIMITED: 'SUPPORT-RATE-LIMITED',
  SUPPORT_BUSY: 'SUPPORT-BUSY',
  SUPPORT_VOICE_UNCLEAR: 'SUPPORT-VOICE-UNCLEAR',
  SUPPORT_VOICE_UNAVAILABLE: 'SUPPORT-VOICE-UNAVAILABLE',
}

// What each area's task is called when nothing more specific is known: "We couldn't <task>".
const AREA_TASKS = {
  AUTH: 'sign you in',
  ACCOUNT: 'save your details',
  MENU: 'load the menu',
  ORDER: 'place your order',
  PAYMENT: 'take the payment',
  VOUCHER: 'apply this voucher',
  DELIVERY: 'load tracking for this order',
  SUPPORT: 'send your message',
  SERVER: 'finish that',
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const pad = value => (value < 10 ? `0${value}` : `${value}`)

/** "26 Oct 12:41", local time. */
export function formatReferenceTime(at) {
  const date = at instanceof Date ? at : new Date(at)
  if (isNaN(date.getTime())) {
    return ''
  }
  return `${date.getDate()} ${MONTHS[date.getMonth()]} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

// Long ids (UUIDs, correlation ids) show their first 8 characters; a short reference made to be
// read out (e.g. "ORD-7K3P9Q") shows whole. The full value is always kept for copying.
const SHORT_ID_LIMIT = 12

export function shortServerId(id) {
  const value = id === undefined || id === null ? '' : String(id).trim()
  if (!value) {
    return ''
  }
  return value.length > SHORT_ID_LIMIT ? value.slice(0, 8) : value
}

/** "CODE · 26 Oct 12:41 · 7f3a9c21" (the screen form; without "Reference: "). */
export function formatReference(code, at, serverId) {
  return [code, formatReferenceTime(at), shortServerId(serverId)].filter(Boolean).join(' · ')
}

/** The same line with the full server id: what the copy action and the support message carry. */
export function formatFullReference(code, at, serverId) {
  const id = serverId === undefined || serverId === null ? '' : String(serverId).trim()
  return [code, formatReferenceTime(at), id].filter(Boolean).join(' · ')
}

const present = value => value !== undefined && value !== null && value !== ''

function readHeader(response, name) {
  try {
    return response && response.headers && typeof response.headers.get === 'function'
      ? response.headers.get(name) || undefined
      : undefined
  } catch (e) {
    return undefined
  }
}

function bodyOf(error) {
  if (!error || typeof error !== 'object') {
    return {}
  }
  const data = error.data !== undefined ? error.data : error.body
  if (data && typeof data === 'object') {
    return data
  }
  return error
}

/**
 * The server's id for this failure: a dedicated support reference first (body, then
 * X-Support-Reference), then the request's X-Correlation-ID. Only response evidence is read,
 * never a correlation id the page itself sent.
 */
export function serverIdOf(error) {
  if (!error || typeof error !== 'object') {
    return undefined
  }
  const body = bodyOf(error)
  const candidates = [
    body.supportReference,
    body.support_reference,
    error.supportReference,
    error.supportReferenceHeader,
    readHeader(error.response, 'X-Support-Reference'),
    error.responseCorrelationId,
    readHeader(error.response, 'X-Correlation-ID'),
  ]
  for (let index = 0; index < candidates.length; index += 1) {
    if (present(candidates[index]) && typeof candidates[index] !== 'object') {
      return String(candidates[index])
    }
  }
  return undefined
}

export function statusOf(error) {
  if (!error || typeof error !== 'object') {
    return undefined
  }
  const candidates = [
    error.status,
    error.responseStatus,
    error.response && error.response.status,
    error.statusCode,
    error.data && error.data.statusCode,
  ]
  for (let index = 0; index < candidates.length; index += 1) {
    const value = Number(candidates[index])
    if (candidates[index] !== undefined && candidates[index] !== null && !isNaN(value) && value > 0) {
      return value
    }
  }
  return undefined
}

const isOffline = () => typeof navigator !== 'undefined' && navigator.onLine === false

/**
 * What kind of failure this is, before the area says what it means:
 * 'offline' | 'timeout' | 'unreachable' | 'server' | 'signed-out' | 'http' | 'unknown'.
 */
export function failureKind(error) {
  if (!error) {
    return 'unknown'
  }
  const status = statusOf(error)
  const name = error.name || (error.cause && error.cause.name)
  if (error.isTimeout || error.code === 'TIMEOUT' || name === 'AbortError' || name === 'TimeoutError' ||
    status === 408 || status === 504) {
    return 'timeout'
  }
  const networkFailure = error.isNetworkError || error.code === 'NETWORK_ERROR' ||
    (name === 'TypeError' && !status)
  if (networkFailure) {
    return error.offline || isOffline() ? 'offline' : 'unreachable'
  }
  if (error.code === 'AUTHENTICATION_REQUIRED' || status === 401) {
    return 'signed-out'
  }
  if (status >= 500) {
    return 'server'
  }
  if (status) {
    return 'http'
  }
  return 'unknown'
}

const TRANSPORT = {
  offline: {
    code: ERROR_CODES.NETWORK_OFFLINE,
    title: "You're offline",
    message: 'Your device has lost its internet connection.',
    guidance: 'Check your connection and try again.',
  },
  timeout: {
    code: ERROR_CODES.NETWORK_TIMEOUT,
    title: 'This is taking too long',
    message: 'Lunch.Team took too long to answer.',
    guidance: 'Check your connection and try again.',
  },
  unreachable: {
    code: ERROR_CODES.SERVER_UNAVAILABLE,
    title: "We couldn't reach Lunch.Team",
    message: 'Your connection or our service may be down.',
    guidance: 'Check your connection and try again in a minute.',
  },
  server: {
    code: ERROR_CODES.SERVER_UNAVAILABLE,
    title: "Lunch.Team isn't responding",
    message: 'Our service is having a problem right now.',
    guidance: 'Try again in a few minutes.',
  },
  'signed-out': {
    code: ERROR_CODES.AUTH_SIGNED_OUT,
    title: 'Please sign in again',
    message: 'Your session has expired.',
    guidance: 'Sign in again to carry on.',
  },
}

const PAYMENT_UNKNOWN = {
  code: ERROR_CODES.PAYMENT_UNKNOWN_STATE,
  title: "We couldn't confirm your payment",
  message: "We don't know yet whether the payment went through.",
  guidance: "Don't pay again yet. Check My Orders in a few minutes, or contact support with the reference below.",
}

// Next steps that must never be shown when retrying could charge or order twice.
const RETRY_WORDS = /try again|retry/i
const NO_RETRY_GUIDANCE = 'Check My Orders before doing anything else, or contact support with the reference below.'

/**
 * @param {*} error a request-layer error (status, code, data, response), a network failure, a timeout,
 *   or nothing (a failure the page detected itself)
 * @param {{area?: string, task?: string, title?: string, message?: string, guidance?: string,
 *   code?: string, retrySafe?: boolean, paymentUnknown?: boolean, at?: Date, keepTitle?: boolean}} options
 *   area   catalogue area (ORDER, ACCOUNT, ...) for codes and the default title
 *   task   "save your details": the title becomes "We couldn't save your details"
 *   title/message/guidance/code  what the caller knows about this failure (wins over the defaults
 *          for an ordinary HTTP failure; offline, slow, server down and signed out keep their own
 *          code and next step, under the caller's title)
 *   retrySafe false: never invite a retry (payments, orders that may exist)
 *   paymentUnknown true: the payment's outcome is unknown (PAYMENT-UNKNOWN-STATE wins)
 */
export function userError(error, options = {}) {
  const area = String(options.area || 'SERVER').toUpperCase()
  const task = options.task || AREA_TASKS[area] || AREA_TASKS.SERVER
  const taskTitle = options.title || `We couldn't ${task}`
  const status = statusOf(error)
  const serverId = options.serverId || serverIdOf(error)
  const at = options.at || (error && error.at) || new Date()
  const kind = failureKind(error)

  let copy
  if (options.paymentUnknown) {
    copy = { ...PAYMENT_UNKNOWN }
  } else if (TRANSPORT[kind]) {
    const transport = TRANSPORT[kind]
    copy = {
      code: transport.code,
      // The task stays in the title when the caller named one ("We couldn't send your message").
      title: options.title || options.task ? taskTitle : transport.title,
      message: transport.message,
      guidance: transport.guidance,
    }
  } else {
    const fallbackCode = kind === 'http'
      ? `${area}-UNEXPECTED-${status}`
      : area === 'SERVER' ? ERROR_CODES.SERVER_UNEXPECTED : `${area}-UNEXPECTED`
    copy = {
      code: options.code || fallbackCode,
      title: taskTitle,
      message: options.message || '',
      guidance: options.guidance || 'Try again, or contact support with the reference below.',
    }
  }

  const retrySafe = options.paymentUnknown ? false : options.retrySafe
  if (retrySafe === false && !options.paymentUnknown && RETRY_WORDS.test(copy.guidance)) {
    copy.guidance = options.guidance && !RETRY_WORDS.test(options.guidance)
      ? options.guidance
      : NO_RETRY_GUIDANCE
  }

  return {
    title: copy.title,
    message: copy.message,
    guidance: copy.guidance,
    code: copy.code,
    reference: formatReference(copy.code, at, serverId),
    fullReference: formatFullReference(copy.code, at, serverId),
    serverId,
    at,
    status,
    retrySafe: retrySafe === undefined ? kind !== 'signed-out' : retrySafe,
    kind,
    isUserError: true,
  }
}

/** A failure the page found itself (no request involved), in the same shape. */
export function knownError(code, copy = {}) {
  return userError(null, { ...copy, code, area: String(code).split('-')[0] })
}

/**
 * Anything handed to an error display (a userError, a checkout error from mapCheckoutError,
 * or a legacy string) in the standard shape. Strings are never shown as the title.
 */
export function toDisplayError(value, fallback = {}) {
  if (value && typeof value === 'object' && value.isUserError) {
    return value
  }
  if (value && typeof value === 'object' && (value.title || value.message)) {
    const code = value.referenceCode || fallback.code || ERROR_CODES.SERVER_UNEXPECTED
    const at = value.at || fallback.at || new Date()
    const serverId = value.serverId || value.supportReference
    return {
      ...value,
      title: value.title || fallback.title ||
        `We couldn't ${fallback.task || AREA_TASKS[String(fallback.area || 'SERVER').toUpperCase()] || AREA_TASKS.SERVER}`,
      code,
      reference: formatReference(code, at, serverId),
      fullReference: formatFullReference(code, at, serverId),
      serverId,
      at,
      isUserError: true,
    }
  }
  return userError(null, {
    area: fallback.area,
    task: fallback.task,
    title: fallback.title,
    code: fallback.code,
    message: typeof value === 'string' ? value : '',
    guidance: fallback.guidance,
    at: fallback.at,
  })
}

/**
 * Fixed copy for a display that renders it later (a modal's errorMessage prop): the reference time
 * is taken when it is first shown.
 */
export const errorCopy = (code, copy) => ({ ...copy, referenceCode: code })



// WEBPACK FOOTER //
// ./src/utils/userError.js